'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import RatingStars from '@repo/ui/src/components/RatingStars';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import Image from 'next/image';
import { productToAnalytics } from '@repo/ui/src/services/analytics/factories';
import { productClicked, ProductClicked } from '@repo/ui/src/services/analytics';
import { ProductProps } from '@repo/ui/src/types';

interface LearnMoreProps {
  product: ProductProps;
}

const LearnMore = ({ product }: LearnMoreProps) => {
  const { t } = useTranslation();
  const { close } = useModal(ModalID.learnMore);
  const { push, eCommEnabled, locale } = useCurrentUrl();

  const learnMoreClick = () => {
    const clickProps = {
      ...productToAnalytics(product, locale),
      list_name: 'learn more recipes',
      basket_token: '',
    } as ProductClicked;
    productClicked(clickProps);
    push(product.productUrl);
    close();
  };

  return (
    <div className="pb-12 px-16">
      {product?.mainImageUrl && (
        <div className="mb-8 flex justify-center">
          <Image src={product?.mainImageUrl} alt={product?.title} width={280} height={280} />
        </div>
      )}

      <h2 className="tra-text-web-heading-4 mb-2">{product?.title}</h2>

      {eCommEnabled && (
        <div className="flex gap-4 mb-4 items-center justify-start">
          {product?.ratingSummary?.average && (
            <>
              <RatingStars
                value={product?.ratingSummary?.average}
                reviewCount={product?.ratingSummary?.count}
                className="[&_svg]:w-[18px]"
                hideValue
                showCountInParentheses
              />
              <span className="self-stretch border-l border-warm-400" />
            </>
          )}
          <p itemProp="sku" className="text-warm-400 leading-none">
            {product?.sku}
          </p>
        </div>
      )}

      <p className="tra-text-web-body-l mb-4">
        {product?.description || product?.shortDescription}
      </p>

      <Button
        variant={ButtonVariant.secondary}
        size="large"
        className="mb-4 w-full"
        onClick={learnMoreClick}
      >
        {t('productDetailPageContent.learnMoreText', undefined, { default: 'Learn More' })}
      </Button>
    </div>
  );
};

export default LearnMore;
